// Swipe System Constants
import { BetLevelDefinition, PersonalitySuitInfo, BetLevel, PersonalitySuit } from '../types/swipe';

// Bet Level Definitions
export const BET_LEVELS: Record<BetLevel, BetLevelDefinition> = {
  low: {
    id: 'low',
    label: 'Coffee & Casual',
    description: "Let's start low-key and see if there's chemistry",
    icon: '☕',
    suggestedActivities: [
      '☕ Coffee shop meet-up',
      '🚶 Walk in the park',
      '🧃 Juice bar / casual cafe',
      '🎨 Browse a local market'
    ],
    characteristics: {
      commitment: 'LOW',
      duration: '30-60 minutes',
      timeOfDay: 'Flexible, usually daytime',
      cost: '$5-15',
      pressure: 'Minimal'
    },
    messaging: {
      toSender: "You're suggesting a casual first meeting",
      toReceiver: "They want to start casual - no pressure!",
      conversationStarter: "Let's grab coffee and chat - what's your favorite spot?"
    },
    psychologicalSignal: "I'm interested but cautious / Want to feel it out first",
    idealFor: [
      'First online dating attempts',
      'Busy schedules',
      'Want multiple casual dates',
      'Low-pressure personality types'
    ]
  },
  medium: {
    id: 'medium',
    label: 'Dinner Date or Activity',
    description: "I'm genuinely interested in getting to know you",
    icon: '🍷',
    suggestedActivities: [
      '🍝 Dinner at a nice restaurant',
      '🎭 Show/concert + drinks',
      '🎳 Fun activity (bowling, mini golf, etc.)',
      '🍷 Wine tasting',
      '🎨 Museum or gallery + dinner'
    ],
    characteristics: {
      commitment: 'MEDIUM',
      duration: '2-3 hours',
      timeOfDay: 'Evening preferred',
      cost: '$30-80',
      pressure: 'Moderate'
    },
    messaging: {
      toSender: "You're showing real interest and investment",
      toReceiver: "They're ready to invest time in getting to know you!",
      conversationStarter: "I'd love to take you out - dinner or something fun?"
    },
    psychologicalSignal: "I'm serious about dating / I see real potential here",
    idealFor: [
      'High compatibility matches (85%+)',
      'Shared interests alignment',
      'Ready for relationship',
      'Want meaningful connection'
    ]
  },
  high: {
    id: 'high',
    label: 'Serious Intent / Video First',
    description: "I think we're a great match - let's do this right",
    icon: '🎯',
    suggestedActivities: [
      '📹 Video chat first (30 min)',
      '🎯 Planned date experience',
      '✈️ Weekend trip discussion',
      '💑 Meeting with clear relationship intent'
    ],
    characteristics: {
      commitment: 'HIGH',
      duration: 'Flexible / Long-term thinking',
      timeOfDay: 'Planned in advance',
      cost: 'Investment mindset',
      pressure: 'Higher expectations'
    },
    messaging: {
      toSender: "You're signaling serious relationship interest",
      toReceiver: "They see real potential and want to invest properly!",
      conversationStarter: "I think we could be great together - let's video chat first?"
    },
    psychologicalSignal: "I'm looking for something real / This feels special",
    idealFor: [
      'Exceptional matches (90%+)',
      'Ready for serious relationship',
      'High personality compatibility',
      'Value authenticity',
      'Want to avoid time-wasters'
    ]
  }
};

// Alias for backward compatibility
export const BET_LEVEL_DETAILS = BET_LEVELS;

// Personality Suit Definitions
export const PERSONALITY_SUITS: Record<PersonalitySuit, PersonalitySuitInfo> = {
  hearts: {
    suit: 'hearts',
    name: 'The Romantics',
    traits: 'Emotional, Expressive, Relationship-Focused',
    lookingFor: 'Deep connections, long-term romance',
    communicationStyle: 'Warm, affectionate, open',
    dateIdeas: 'Intimate dinners, sunset walks, art galleries',
    colorTheme: 'linear-gradient(135deg, #FF6B9D 0%, #C06C84 100%)',
    icon: '♥️'
  },
  diamonds: {
    suit: 'diamonds',
    name: 'The Achievers',
    traits: 'Ambitious, Goal-Oriented, Success-Driven',
    lookingFor: 'Power couples, career-minded partners',
    communicationStyle: 'Direct, efficient, strategic',
    dateIdeas: 'Networking events, fine dining, adventure sports',
    colorTheme: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
    icon: '♦️'
  },
  clubs: {
    suit: 'clubs',
    name: 'The Social Butterflies',
    traits: 'Outgoing, Community-Focused, Fun-Loving',
    lookingFor: 'Social partners, friendship first',
    communicationStyle: 'Playful, friendly, enthusiastic',
    dateIdeas: 'Group activities, concerts, festivals',
    colorTheme: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)',
    icon: '♣️'
  },
  spades: {
    suit: 'spades',
    name: 'The Intellectuals',
    traits: 'Thoughtful, Creative, Deep Thinkers',
    lookingFor: 'Mental connection, meaningful conversations',
    communicationStyle: 'Thoughtful, inquisitive, philosophical',
    dateIdeas: 'Museums, bookstores, coffee shops, workshops',
    colorTheme: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
    icon: '♠️'
  },
  joker: {
    suit: 'joker',
    name: 'The Wild Cards',
    traits: 'Unpredictable, Eclectic, Don\'t Fit One Category',
    lookingFor: 'Open to anything, adventurous',
    communicationStyle: 'Spontaneous, varied, surprising',
    dateIdeas: 'Surprise me!',
    colorTheme: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 50%, #FFE66D 100%)',
    icon: '🃏'
  }
};

// System Constants
export const SWIPE_CONSTANTS = {
  KEEP_PILE_MAX: 5,
  DAILY_DRAWS_MAX: 30,
  CARD_EXPIRATION_HOURS: 72, // 3 days
  MATCH_REQUEST_EXPIRATION_HOURS: 48, // 2 days
  REFRESH_TOKENS_PER_DAY: 1,
  VIDEO_DURATION_MIN: 5,
  VIDEO_DURATION_MAX: 6,
  MESSAGE_MAX_LENGTH: 300,
  NOTES_MAX_LENGTH: 200,
  MATCH_REQUESTS_DAILY_MAX: 10
};

// Card Animation Durations (ms)
export const ANIMATION_DURATIONS = {
  CARD_FLIP: 600,
  CARD_SWIPE: 300,
  CARD_DISCARD: 400,
  CARD_KEEP: 500,
  CARD_APPEAR: 300,
  MATCH_CELEBRATION: 2000
};

// Swipe Gesture Thresholds
export const SWIPE_THRESHOLDS = {
  LEFT_DISCARD: -150, // pixels
  RIGHT_SKIP: 150, // pixels
  UP_KEEP: -100, // pixels
  VELOCITY_MIN: 0.5 // minimum swipe velocity
};

// Notification Messages
export const NOTIFICATION_MESSAGES = {
  KEEP_PILE_FULL: 'Keep pile full! Play or remove cards to add more.',
  CARD_EXPIRED: (name: string) => `${name}'s card has expired and returned to deck.`,
  CARD_EXPIRES_24H: (name: string) => `${name}'s card expires in 24 hours!`,
  CARD_EXPIRES_6H: (name: string) => `Last chance! ${name}'s card expires soon.`,
  CARD_EXPIRES_1H: (name: string) => `⏰ ${name}'s card expires in 1 hour!`,
  MATCH_SUCCESS: "🎉 It's a Match! You're both in!",
  MATCH_DECLINED: (name: string) => `${name} didn't match this time. Keep playing!`,
  MATCH_TIMEOUT: (name: string) => `${name} didn't respond in time`,
  DAILY_LIMIT_REACHED: 'You\'ve reached your daily swipe limit! Come back tomorrow.',
  SOMEONE_PLAYED: 'Someone played a card! 🎴'
};

// Color Themes
export const CARD_COLORS = {
  PRIMARY: '#667eea',
  SECONDARY: '#764ba2',
  SUCCESS: '#4ECDC4',
  WARNING: '#FFE66D',
  ERROR: '#FF6B6B',
  HEARTS: '#FF6B9D',
  DIAMONDS: '#FFD700',
  CLUBS: '#2ECC71',
  SPADES: '#667EEA',
  JOKER: '#FF6B6B'
};

// Card Pattern Designs (for card backs)
export const CARD_PATTERNS = {
  hearts: 'repeating-linear-gradient(45deg, rgba(255, 107, 157, 0.1) 0px, rgba(255, 107, 157, 0.1) 10px, transparent 10px, transparent 20px)',
  diamonds: 'repeating-linear-gradient(45deg, rgba(255, 215, 0, 0.1) 0px, rgba(255, 215, 0, 0.1) 10px, transparent 10px, transparent 20px)',
  clubs: 'repeating-linear-gradient(45deg, rgba(46, 204, 113, 0.1) 0px, rgba(46, 204, 113, 0.1) 10px, transparent 10px, transparent 20px)',
  spades: 'repeating-linear-gradient(45deg, rgba(102, 126, 234, 0.1) 0px, rgba(102, 126, 234, 0.1) 10px, transparent 10px, transparent 20px)',
  joker: 'repeating-linear-gradient(45deg, rgba(255, 107, 107, 0.1) 0px, rgba(255, 107, 107, 0.1) 10px, transparent 10px, transparent 20px)'
};

